import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';

import { ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from '@/pages/account';
import { AuthPage } from '@/pages/auth';
import { DeckPage } from '@/pages/deck';
import { DesignPage } from '@/pages/design';
import { HealthPage } from '@/pages/health';
import { ImportDeckPage } from '@/pages/import-deck';
import { DecksPage } from '@/pages/decks';
import { LandingPage } from '@/pages/landing';
import { LearnPage } from '@/pages/learn';
import { NewDeckPage } from '@/pages/new-deck';
import { NotFoundPage } from '@/pages/not-found';
import { PrivacyPage, TermsPage } from '@/pages/legal';
import { ReviewPage } from '@/pages/review';
import { SettingsPage } from '@/pages/settings';
import { StatusPage } from '@/pages/status';
import { TodayPage } from '@/pages/today';
import { ApiError } from '@/shared/api/client';

import { Home, Layout, RequireGuest, RequireSession } from './Layout';

/**
 * Builds the query client.
 *
 * Created inside the component rather than at module scope so that every test
 * gets its own cache; a shared client makes tests pass or fail depending on
 * the order they run in.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // A review queue that silently refetches under the user mid-answer is
        // worse than a slightly stale one.
        refetchOnWindowFocus: false,
        retry: (failures, error) =>
          failures < 1 && !(error instanceof ApiError && error.status < 500),
        staleTime: 30_000,
      },
    },
  });
}

export function App(): React.JSX.Element {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Home signedIn={<TodayPage />} guest={<LandingPage />} />} />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/design" element={<DesignPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route element={<RequireGuest />}>
              <Route path="/login" element={<AuthPage mode="login" />} />
              <Route path="/register" element={<AuthPage mode="register" />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            </Route>
            <Route element={<RequireSession />}>
              <Route path="/decks" element={<DecksPage />} />
              <Route path="/decks/new" element={<NewDeckPage />} />
              <Route path="/decks/import" element={<ImportDeckPage />} />
              <Route path="/decks/:deckId" element={<DeckPage tab="cards" />} />
              <Route path="/decks/:deckId/health" element={<DeckPage tab="health" />} />
              <Route path="/decks/:deckId/settings" element={<DeckPage tab="settings" />} />
              <Route path="/health" element={<HealthPage />} />
              <Route path="/review" element={<LearnPage />} />
              <Route path="/review/:scope" element={<ReviewPage />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
